import { useGSAP } from "@gsap/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Dialog, Modal, ModalOverlay } from "react-aria-components";
import gsap from "gsap";

import backIcon from "@/assets/figma/chat-attachments/back.svg";
import dividerIcon from "@/assets/figma/chat-attachments/divider.svg";
import downloadIcon from "@/assets/figma/chat-attachments/download.svg";
import removeIcon from "@/assets/figma/chat-attachments/remove.svg";
import documentIcon from "@/assets/figma/chat-file-drop/document-file.svg";
import imageIcon from "@/assets/figma/chat-file-drop/image-file.svg";
import videoIcon from "@/assets/figma/chat-file-drop/video-file.svg";
import type { ChatAttachment } from "@/features/chat/chatAttachments";

gsap.registerPlugin(useGSAP);

interface ChatAttachmentPreviewOverlayProps {
  attachments: ReadonlyArray<ChatAttachment>;
  onClose: () => void;
  onRemove?: (attachmentId: string) => void;
  onRequestDownloadUrl: (resourceId: string) => Promise<string>;
  onRequestPreviewUrl: (resourceId: string) => Promise<string>;
  onSelect: (attachmentId: string) => void;
  previewUrls: Readonly<Record<string, string>>;
  selectedAttachmentId: string;
}

function prefersReducedMotion() {
  return (
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

function attachmentTypeIcon(type: ChatAttachment["resourceType"]) {
  if (type === "image") return imageIcon;
  if (type === "video") return videoIcon;
  return documentIcon;
}

function triggerBrowserDownload(url: string, displayName: string) {
  const anchor = document.createElement("a");
  anchor.download = displayName;
  anchor.href = url;
  anchor.rel = "noopener noreferrer";
  anchor.style.display = "none";
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
}

export function ChatAttachmentPreviewOverlay({
  attachments,
  onClose,
  onRemove,
  onRequestDownloadUrl,
  onRequestPreviewUrl,
  onSelect,
  previewUrls,
  selectedAttachmentId,
}: ChatAttachmentPreviewOverlayProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const isClosingRef = useRef(false);
  const [downloadState, setDownloadState] = useState<
    "error" | "idle" | "loading"
  >("idle");
  const [previewErrorId, setPreviewErrorId] = useState("");

  const selectedAttachment = useMemo(
    () =>
      attachments.find(
        (attachment) => attachment.attachmentId === selectedAttachmentId,
      ) ?? null,
    [attachments, selectedAttachmentId],
  );
  const selectedPreviewUrl =
    selectedAttachment?.resourceId &&
    previewUrls[selectedAttachment.resourceId]
      ? previewUrls[selectedAttachment.resourceId]
      : "";

  const previewResourceId = selectedAttachment?.resourceId;
  const canRequestPreview = selectedAttachment?.resourceType === "image" && selectedAttachment.status === "ready";
  useEffect(() => {
    if (!previewResourceId || !canRequestPreview || selectedPreviewUrl) return;
    void onRequestPreviewUrl(previewResourceId).catch(() => {
      setPreviewErrorId(previewResourceId);
    });
  }, [onRequestPreviewUrl, previewResourceId, canRequestPreview, selectedPreviewUrl]);

  useGSAP(
    () => {
      const overlay = overlayRef.current;
      const dialog = dialogRef.current;
      if (!overlay || !dialog || prefersReducedMotion()) return;
      gsap.fromTo(
        overlay,
        { opacity: 0 },
        { opacity: 1, duration: 0.22, ease: "power2.out" },
      );
      gsap.fromTo(
        dialog,
        { opacity: 0, scale: 0.985 },
        {
          opacity: 1,
          duration: 0.32,
          ease: "power3.out",
          scale: 1,
          onComplete: () => { if (!dialog.contains(document.activeElement)) dialog.focus(); },
        },
      );
    },
    { scope: overlayRef },
  );

  const close = useCallback(() => {
    if (isClosingRef.current) return;
    const overlay = overlayRef.current;
    const dialog = dialogRef.current;
    if (!overlay || !dialog || prefersReducedMotion()) {
      onClose();
      return;
    }
    isClosingRef.current = true;
    gsap.killTweensOf([overlay, dialog]);
    gsap
      .timeline({
        onComplete: onClose,
        onInterrupt: onClose,
      })
      .to(dialog, {
        autoAlpha: 0,
        duration: 0.16,
        ease: "power2.in",
        scale: 0.99,
      })
      .to(
        overlay,
        { autoAlpha: 0, duration: 0.16, ease: "power2.in" },
        "<",
      );
  }, [onClose]);

  useEffect(() => {
    const dialog = dialogRef.current;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") { event.stopPropagation(); close(); }
    };
    dialog?.addEventListener("keydown", onKeyDown);
    return () => dialog?.removeEventListener("keydown", onKeyDown);
  }, [close]);

  const removeSelected = () => {
    if (!selectedAttachment) return;
    const selectedIndex = attachments.findIndex(
      (attachment) =>
        attachment.attachmentId === selectedAttachment.attachmentId,
    );
    const remainingImages = attachments.filter(
      (attachment) =>
        attachment.attachmentId !== selectedAttachment.attachmentId &&
        attachment.resourceType === "image" &&
        attachment.status === "ready" &&
        attachment.resourceId,
    );
    const nextAttachment =
      remainingImages.find((attachment) => {
        const index = attachments.findIndex(
          (candidate) => candidate.attachmentId === attachment.attachmentId,
        );
        return index > selectedIndex;
      }) ?? remainingImages.at(-1);

    onRemove?.(selectedAttachment.attachmentId);
    if (nextAttachment) {
      onSelect(nextAttachment.attachmentId);
      return;
    }
    close();
  };

  const downloadSelected = async () => {
    if (!selectedAttachment?.resourceId || downloadState === "loading") return;
    setDownloadState("loading");
    try {
      const url = await onRequestDownloadUrl(selectedAttachment.resourceId);
      triggerBrowserDownload(url, selectedAttachment.displayName);
      setDownloadState("idle");
    } catch {
      setDownloadState("error");
    }
  };

  if (!selectedAttachment) return null;

  return (
    <ModalOverlay
      className="merry-chat-attachment-preview-overlay"
      data-figma-node-id="439:86106"
      data-testid="chat-attachment-preview-overlay"
      isDismissable
      isOpen
      onOpenChange={(isOpen) => {
        if (!isOpen) close();
      }}
      ref={overlayRef}
    >
      <Modal className="merry-chat-attachment-preview-modal">
        <Dialog
          aria-label={`查看附件 ${selectedAttachment.displayName}`}
          className="merry-chat-attachment-preview"
          ref={dialogRef}
        >
          <nav
            aria-label="当前对话附件"
            className="merry-chat-attachment-preview__rail"
          >
            {attachments.map((attachment) => {
              const previewUrl = attachment.resourceId
                ? previewUrls[attachment.resourceId]
                : "";
              const canPreview =
                attachment.resourceType === "image" &&
                attachment.status === "ready" &&
                Boolean(attachment.resourceId);
              const isSelected =
                attachment.attachmentId === selectedAttachmentId;
              return (
                <Button
                  aria-label={
                    canPreview
                      ? `查看 ${attachment.displayName}`
                      : `${attachment.displayName} 暂不支持大图预览`
                  }
                  className="merry-chat-attachment-preview__thumb"
                  data-resource-type={attachment.resourceType}
                  data-selected={isSelected ? "true" : "false"}
                  isDisabled={!canPreview}
                  key={attachment.attachmentId}
                  onPress={() => onSelect(attachment.attachmentId)}
                >
                  {attachment.resourceType === "image" && previewUrl ? (
                    <img
                      alt=""
                      aria-hidden="true"
                      draggable="false"
                      src={previewUrl}
                    />
                  ) : (
                    <span className="merry-chat-attachment-preview__thumb-file">
                      <img
                        alt=""
                        aria-hidden="true"
                        src={attachmentTypeIcon(attachment.resourceType)}
                      />
                      <span>{attachment.displayName}</span>
                    </span>
                  )}
                </Button>
              );
            })}
          </nav>

          <div className="merry-chat-attachment-preview__stage">
            {selectedPreviewUrl && previewErrorId !== selectedAttachment.resourceId ? (
              <img
                alt={selectedAttachment.displayName}
                className="merry-chat-attachment-preview__image"
                draggable="false"
                src={selectedPreviewUrl}
                onError={() => setPreviewErrorId(selectedAttachment.resourceId ?? "")}
              />
            ) : (
              <div
                className="merry-chat-attachment-preview__loading"
                role="status"
              >
                {previewErrorId === selectedAttachment.resourceId
                  ? "预览暂时无法读取"
                  : "正在读取图片"}
              </div>
            )}

            <div className="merry-chat-attachment-preview__actions">
              <Button
                aria-label="返回对话"
                className="merry-chat-attachment-preview__back"
                onPress={close}
              >
                <img alt="" aria-hidden="true" src={backIcon} />
              </Button>
              <div className="merry-chat-attachment-preview__action-group">
                {onRemove ? <> <Button
                  className="merry-chat-attachment-preview__action"
                  onPress={removeSelected}
                >
                  <img alt="" aria-hidden="true" src={removeIcon} />
                  <span>从对话移除</span>
                </Button>
                <img
                  alt=""
                  aria-hidden="true"
                  className="merry-chat-attachment-preview__divider"
                  src={dividerIcon}
                /> </> : null}
                <Button
                  className="merry-chat-attachment-preview__action"
                  isDisabled={downloadState === "loading"}
                  onPress={() => void downloadSelected()}
                >
                  <img alt="" aria-hidden="true" src={downloadIcon} />
                  <span>
                    {downloadState === "loading" ? "生成中" : "下载"}
                  </span>
                </Button>
              </div>
            </div>
            {downloadState === "error" ? (
              <p
                className="merry-chat-attachment-preview__notice"
                role="status"
              >
                下载链接生成失败
              </p>
            ) : null}
          </div>
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}
